/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useState, useEffect, useCallback, ChangeEvent } from 'react'
import classnames from 'classnames'
import { FieldHookConfig, useField, FieldValidator } from 'formik'
import { ChangePasswordValidation } from './components/ChangePasswordValidation/ChangePasswordValidation'
import { GeneratePasswordButton } from './components/GeneratePasswordButton/GeneratePasswordButton'
import { EyeButton } from './components/EyeButton/EyeButton'
import style from './PasswordField.module.css'
import { Field } from '../Field/Field'
import { generatePassword } from '../../utils/generatePassword'

type IFieldProps = {
  label: string
  wrapErrorText?: boolean
  isBlockShowError?: boolean
  errorDataQaLocator?: string
  isWithGenerationButton?: boolean
  generatePasswordButton?: string
  isChangePasswordValidation?: boolean
  inputClassName?: string
  autoFocus?: boolean
  validate?: FieldValidator | ((value: string, phoneFieldType: boolean) => string | void | Promise<string | void>)
  onChangeHandler?(e: ChangeEvent<HTMLInputElement>): void
  onFocusHandler?(e: ChangeEvent<HTMLInputElement>): void
  onBlurHandler?(e: ChangeEvent<HTMLInputElement>): void
  onGenerateButtonClick?(): void
} & FieldHookConfig<string>

export const PasswordField: FC<IFieldProps> = ({
  wrapErrorText,
  errorDataQaLocator,
  isBlockShowError,
  label,
  isWithGenerationButton,
  generatePasswordButton,
  isChangePasswordValidation,
  autoFocus,
  validate,
  onChangeHandler,
  onFocusHandler,
  onBlurHandler,
  onGenerateButtonClick,
  ...props
}) => {
  const [field, meta, helper] = useField(props)
  const [isOpen, isOpenChange] = useState<boolean>(false)
  const [isShowValidation, isShowValidationChange] = useState<boolean>(false)

  useEffect(() => {
    if (meta.touched && isChangePasswordValidation) {
      isShowValidationChange(true)
    }
  }, [meta.touched, isChangePasswordValidation])

  const fieldClassNames = classnames({
    [style.field]: !validate,
  })

  const onEyeButtonClick = () => isOpenChange((value) => !value)

  const onGenerateMouseDown = useCallback(() => {
    helper.setValue(generatePassword())
    helper.setError('')

    if (isChangePasswordValidation) {
      isShowValidationChange(true)
    }

    if (onGenerateButtonClick) {
      onGenerateButtonClick()
    }

    isOpenChange(true)
  }, [helper, isChangePasswordValidation, onGenerateButtonClick])

  const onBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onBlurHandler) {
        onBlurHandler(e)
      }

      // if (isChangePasswordValidation) {
      //   isShowValidationChange(true);
      // }
    },
    [onBlurHandler],
  )

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onChangeHandler) {
        onChangeHandler(e)
      }

      if (isChangePasswordValidation) {
        isShowValidationChange(true)
      }
    },
    [isChangePasswordValidation, onChangeHandler],
  )

  return (
    <div className={style.inputContainer}>
      <div className={style.inputContainer}>
        <Field
          {...props}
          className={fieldClassNames}
          name={field.name}
          label={label}
          isBlockShowError={isBlockShowError}
          autoFocus={autoFocus}
          validate={validate}
          onChangeHandler={onChange}
          onFocusHandler={onFocusHandler}
          onBlurHandler={onBlur}
          type={isOpen ? 'text' : 'password'}
        />
        {isChangePasswordValidation && <ChangePasswordValidation value={field.value} isShow={isShowValidation} />}
        <EyeButton isOpen={isOpen} onClick={onEyeButtonClick} />
      </div>
      {isWithGenerationButton && <GeneratePasswordButton onMouseDown={onGenerateMouseDown} />}
    </div>
  )
}

PasswordField.displayName = 'PasswordField'
